import _ from 'lodash';
import { Record as record, List } from 'immutable';

import { serialize, serializers } from '../helpers/SearchUrls';
import { legsToDisruptionAlertLegs } from '../helpers/DisruptionAlert';
import { getDefaults, getResolvers } from '../helpers/Records';
import { construct, fields, resolve, resolveAll } from '../helpers/LinkMapper';
import { formatDayOfWeek } from '../helpers/DisruptionAlert';
import {
    TransitModes,
    DisruptionAlertWeekdays,
    DisruptionAlertDurationOptions,
    DisruptionAlertDaysOptions,
} from '../constants/AppConstants';
import trainTime from '../lib/TrainTime';
import Customer from './customer/Customer';
import DisruptionAlertLeg from './DisruptionAlertLeg';
import Station from './Station';
import DisruptionAlertMute from './DisruptionAlertMute';


const fieldSchema = {
    id: [fields.number],
    customer: [resolve.as(Customer)],
    isEmailNotification: [fields.boolean, true],
    isCloudMessageNotification: [fields.boolean, false],
    orderId: [fields.number, null],
    tripId: [fields.number, null],
    origin: [resolve.as(Station)],
    destination: [resolve.as(Station)],
    departureDate: [resolve.with(trainTime), null],
    departureTime: [resolve.with(time => trainTime(time, 'HH:mm:ss')), null],
    legs: [resolveAll.as(DisruptionAlertLeg), new List()],
    alertForever: [fields.boolean, false],
    alertFrom: [resolve.with(trainTime), null],
    alertTo: [resolve.with(trainTime), null],
    daysOfWeek: [resolveAll.with(fields.string), new List()],
    mutes: [resolveAll.as(DisruptionAlertMute), new List()],
    isStationAlert: [fields.boolean, false],
};

class DisruptionAlert extends record(
    getDefaults(fieldSchema)
) {
    static fromJSON (json = {}) {
        return construct(DisruptionAlert, json, getResolvers(fieldSchema));
    }

    static createFromOrderJourney ({
        user,
        order,
        orderTrip,
        orderJourney,
        stationFacilityAlert,
        isEmailNotification,
        isCloudMessageNotification,
    }) {
        const legs = legsToDisruptionAlertLegs(orderJourney.legs);

        const journeyData = {
            origin: orderJourney.getOrigin(),
            destination: orderJourney.getDestination(),
            departureDate: orderJourney.getDepartureTime(),
            departureTime: orderJourney.getDepartureTime(),
            legs,
        };

        const optionsData = {
            alertFrom: orderJourney.getDepartureTime(),
            alertTo: orderJourney.getDepartureTime(),
        };

        return new DisruptionAlert({
            customer: user.customer,
            orderId: order.id,
            tripId: parseInt(orderTrip.getCleanId(true)),
            isStationAlert: stationFacilityAlert,
            isEmailNotification,
            isCloudMessageNotification,
            ...journeyData,
            ...optionsData,
        });
    }

    static createFromBuilder ({
        user,
        order,
        orderTrip,
        formData,
        stationFacilityAlert,
        isEmailNotification,
        isCloudMessageNotification,
    }) {
        const { startDate, customEndDate, selectedServices, duration, days, weekdays } = formData;

        const isIndefinite = duration === DisruptionAlertDurationOptions.INFINITE;

        const isOneOff = (duration === DisruptionAlertDurationOptions.ONEOFF) ||
            (duration === DisruptionAlertDurationOptions.CUSTOM && startDate.isSame(customEndDate));

        const dateAdjustedServices = selectedServices.map(service => service);

        const legs = dateAdjustedServices.reduce((legsAcc, service, serviceIndex) => {
            const groupId = ++serviceIndex;
            const serviceLegs = service.journey.legs
                .filter(leg => leg.serviceDetails.mode === TransitModes.TRAIN)
                .map((leg, legIndex) => new DisruptionAlertLeg({
                    trainUid: leg.getTrainUid(),
                    origin: leg.getOriginStation(),
                    destination: leg.getDestinationStation(),
                    departureTime: leg.getDepartureTime(),
                    arrivalTime: leg.getArrivalTime(),
                    groupId,
                    legOrder: ++legIndex,
                }));

            if (serviceLegs) {
                return legsAcc.concat(serviceLegs);
            }

            return legsAcc;
        }, new List());

        const journey = dateAdjustedServices.first().journey;
        const journeyData = {
            origin: journey.getOriginStation(),
            destination: journey.getDestinationStation(),
            ...(isOneOff && { departureDate: journey.getDepartureDateTime() }),
            departureTime: legs.first().departureTime,
            legs,
        };

        let endDate;
        if (isIndefinite) {
            endDate = null;
        } else if (duration === DisruptionAlertDurationOptions.SIXMONTHS) {
            endDate = startDate.clone().add(6, 'months');
        } else if (duration === DisruptionAlertDurationOptions.ONEOFF) {
            endDate = startDate;
        } else if (duration === DisruptionAlertDurationOptions.CUSTOM) {
            endDate = customEndDate;
        }

        let daysOfWeek = new List();
        if (!isOneOff) {
            if (days === DisruptionAlertDaysOptions.WEEKDAYS) {
                daysOfWeek = _.keys(_.pickBy(weekdays));
            } else if (days === DisruptionAlertDaysOptions.SATURDAYS) {
                daysOfWeek = ['SATURDAY'];
            } else if (days === DisruptionAlertDaysOptions.SUNDAYS) {
                daysOfWeek = ['SUNDAY'];
            }
        }

        const optionsData = {
            alertFrom: isIndefinite ? null : startDate,
            alertTo: endDate,
            daysOfWeek,
        };

        return new DisruptionAlert({
            customer: user.customer,
            orderId: order && order.id,
            tripId: orderTrip && parseInt(orderTrip.getCleanId(true)),
            isStationAlert: stationFacilityAlert,
            alertForever: isIndefinite,
            isEmailNotification,
            isCloudMessageNotification,
            ...journeyData,
            ...optionsData,
        });
    }

    toJSON () {
        const getAlertDetailsProp = (alert, path) => _.get(alert, path) || null;
        return serialize({
            disruptionAlertId: (__, alert) => getAlertDetailsProp(alert, 'id'),
            cocsCustomerId: (__, alert) => getAlertDetailsProp(alert, 'customer.id'),
            isEmailNotification: serializers.identity,
            isCloudMessageNotification: serializers.identity,
            orderId: serializers.identity,
            tripId: serializers.identity,
            origin: serializers.stationCrs,
            destination: serializers.stationCrs,
            departureDate: departureDate => departureDate ? serializers.date(departureDate) : null,
            departureTime: departureTime => departureTime ? serializers.time(departureTime) : null,
            legs: legs => legs ? legs.map(leg => leg.toJSON()) : [],
            alertForever: serializers.identity,
            alertFrom: alertFrom => alertFrom ? serializers.date(alertFrom) : null,
            alertTo: alertTo => alertTo ? serializers.date(alertTo) : null,
            daysOfWeek: serializers.identity,
            mutes: mutes => mutes ? mutes.map(mute => mute.toJSON()) : [],
            isStationAlert: serializers.identity,
        }, this);
    }

    matchesOrderTrip (order, orderTrip) {
        const { orderId, tripId } = this;
        return orderId === order.id && tripId === parseInt(orderTrip.getCleanId(true));
    }

    getServices () {
        return this.legs
            .groupBy(leg => leg.groupId)
            .map(service => service.sortBy(leg => leg.legOrder));
    }

    isForever () {
        return this.alertForever;
    }

    isOneOff () {
        return !this.isForever() && this.alertFrom.isSame(this.alertTo);
    }

    getDaysAlertedOn () {
        const { daysOfWeek, departureDate } = this;

        if (daysOfWeek.size === 0) {
            return 'Weekdays';
        }

        if (this.isOneOff()) {
            return departureDate && departureDate.format('dddd');
        } else if (daysOfWeek.equals(new List(_.keys(DisruptionAlertWeekdays)))) {
            return 'Weekdays';
        } else if (daysOfWeek.equals(new List(['SATURDAY']))) {
            return 'Saturdays';
        } else if (daysOfWeek.equals(new List(['SUNDAY']))) {
            return 'Sundays';
        } else {
            return daysOfWeek.size > 1
                ? daysOfWeek.map(day => formatDayOfWeek(day, true)).join(', ')
                : formatDayOfWeek(daysOfWeek.first()) + 's';
        }
    }

    getOriginAndDestination() {
        return ({
            origin: this.origin.name,
            destination: this.destination.name,
        });
    }

    isMuted () {
        return !!this.mutes.size;
    }

    getMutedUntil () {
        return this.isMuted() && this.mutes.first().muteUntil;
    }
}

export default DisruptionAlert;



// WEBPACK FOOTER //
// ./src/records/DisruptionAlert.js